"use client";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { Monitor, Moon, Sun } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { cn } from "../lib/utils.js";

const themes = [
  {
    key: "system",
    icon: Monitor,
    label: "System theme",
  },
  {
    key: "light",
    icon: Sun,
    label: "Light theme",
  },
  {
    key: "dark",
    icon: Moon,
    label: "Dark theme",
  },
];

export interface ThemeSwitcherProps
{
  readonly value?: "light" | "dark" | "system";
  readonly onChange?: (theme: "light" | "dark" | "system") => void;
  readonly defaultValue?: "light" | "dark" | "system";
  readonly className?: string;
  readonly storageKey?: string;
}
export const ThemeSwitcher = ({
  value,
  onChange,
  defaultValue = "system",
  className,
  storageKey = "theme",
}: ThemeSwitcherProps) =>
{
  const [theme, setTheme] = useControllableState({
    defaultProp: defaultValue,
    prop: value,
    onChange,
  });

  const [mounted, setMounted] = useState(false);

  const handleThemeClick = useCallback(
    (themeKey: "light" | "dark" | "system") =>
    {
      setTheme(themeKey);
      // Persist to localStorage when not controlled
      if (!value)
      {
        localStorage.setItem(storageKey, themeKey);
      }
    },
    [setTheme, value, storageKey]
  );

  // Load from localStorage on mount when not controlled
  useEffect(() =>
  {
    if (!value)
    {
      const storedTheme = localStorage.getItem(storageKey) as "light" | "dark" | "system";
      if (storedTheme && ["light", "dark", "system"].includes(storedTheme))
      {
        setTheme(storedTheme);
      }
    }
    setMounted(true);
  }, [value, storageKey, setTheme]);

  if (!mounted)
  {
    return null;
  }
  return (
    <div className={cn("relative isolate flex h-8 rounded-full bg-background p-1 ring-1 ring-border", className)}>
      {themes.map(({ key, icon: Icon, label }) =>
      {
        const isActive = theme === key;
        return (
          <button
            aria-label={label}
            className="relative h-6 w-6 rounded-full"
            key={key}
            onClick={() => handleThemeClick(key as "light" | "dark" | "system")}
            type="button">
            {isActive && (
              <motion.div
                className="absolute inset-0 rounded-full bg-secondary"
                layoutId="activeTheme"
                transition={{ type: "spring", duration: 0.5 }}
              />
            )}
            <Icon
              className={cn("relative z-10 m-auto h-4 w-4", isActive ? "text-foreground" : "text-muted-foreground")}
            />
          </button>
        );
      })}
    </div>
  );
};
